import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setSubmitting,
  setCashDrawerList,
  setCashDrawerForm,
  updateCashDrawerField,
  setShowRegistrationModal,
  setShowEditModal,
  closeRegistrationModalAndReset,
  closeEditModalAndReset,
  updateCashDrawerStatus,
} from '../../store/slices/hardware/cashDrawerSlice';

import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import useModals from '../useModals';

import CashDrawerService from '../../services/hardware/CashDrawerService';
import { CashDrawerRegistrationForm } from '../../types/hardware/CashDrawerTypes';

const useCashDrawer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();

  const {
    form,
    list,
    isSubmitting,
    error,
    showRegistrationModal,
    showEditModal,
  } = useSelector((state: RootState) => state.cashDrawer);

  const fetchAllCashDrawers = async () => {
    try {
      showLoadingIndicator();
      const response = await CashDrawerService.getAll();
      const { data } = response;

      dispatch(setCashDrawerList(data));
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const fetchCashDrawerById = async (id: number) => {
    try {
      showLoadingIndicator();
      const response = await CashDrawerService.getById(id);
      const { data } = response;

      dispatch(setCashDrawerForm(data));
      dispatch(setShowEditModal(true));
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const saveCashDrawer = async (form: CashDrawerRegistrationForm) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      let response;

      if ((form as any).id) {
        response = await CashDrawerService.update((form as any).id, form);
      } else {
        response = await CashDrawerService.create(form);
      }

      const { title, message, type } = response;
      openModal(type, { title, message });

      if ((form as any).id) {
        dispatch(closeEditModalAndReset());
      } else {
        dispatch(closeRegistrationModalAndReset());
      }

      await fetchAllCashDrawers();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };

  const toggleStatus = async (id: number) => {
    try {
      const response = await CashDrawerService.toggleStatus(id);
      const { title, message, type, data } = response;

      showAlert(type, title, message);
      dispatch(updateCashDrawerStatus(data));
    } catch (error) {
      handle(error);
    }
  };

  const updateField = <K extends keyof CashDrawerRegistrationForm>(
    field: K,
    value: CashDrawerRegistrationForm[K]
  ) => {
    dispatch(updateCashDrawerField({ field, value }));
  };

  const openRegistrationModal = () => {
    dispatch(setShowRegistrationModal(true));
  };

  const closeRegistrationModal = () => {
    dispatch(closeRegistrationModalAndReset());
  };

  return {
    form,
    list,
    isSubmitting,
    error,
    showRegistrationModal,
    showEditModal,

    updateField,
    fetchAllCashDrawers,
    fetchCashDrawerById,
    saveCashDrawer,
    toggleStatus,

    openRegistrationModal,
    closeRegistrationModal,
  };
};

export default useCashDrawer;
